import React, { useState, useEffect, useRef } from 'react';
import { Menu, X } from 'lucide-react';

interface NavbarProps {
  onJoinWaitlist?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onJoinWaitlist }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    // Lightweight CSS-based entrance to avoid bundling GSAP here
    const t = setTimeout(() => setMounted(true), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const navLinks = [
    { href: '#about', label: 'Our Story' },
    { href: '#products', label: 'Products' },
    { href: '#philosophy', label: 'Philosophy' },
    { href: '#testimonials', label: 'Reviews' },
    { href: '#faq', label: 'FAQ' },
  ];

  return (
    <>
      <header
        ref={navRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 transform ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-6'
        } ${
          scrolled
            ? 'bg-[#FAF8F5] shadow-[0_2px_24px_rgba(41,57,50,0.07)] border-b border-[#E8DDD4]/50'
            : 'bg-transparent'
        }`}
        role="banner"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="flex items-center justify-between h-[72px] lg:h-20">

            {/* Logo */}
            <a
              href="#"
              className="flex-shrink-0"
              aria-label="Salford & Co. — home"
            >
              <span className={`font-display text-lg sm:text-xl font-bold tracking-[0.12em] transition-colors duration-500 ${
                mobileOpen ? 'text-[#FAF8F5]' : scrolled ? 'text-[#293932]' : 'text-[#FAF8F5]'
              }`}>
                SALFORD & CO.
              </span>
            </a>

            {/* Desktop Nav — centered */}
            <nav className="hidden lg:flex items-center gap-1" aria-label="Main navigation">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className={`relative px-4 py-2 font-mono text-[11px] uppercase tracking-[0.22em] transition-all duration-300 group ${
                    scrolled
                      ? 'text-[#293932]/60 hover:text-[#293932]'
                      : 'text-[#FAF8F5]/65 hover:text-[#FAF8F5]'
                  }`}
                >
                  {link.label}
                  <span className={`absolute bottom-0 left-4 right-4 h-[1px] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left ${
                    scrolled ? 'bg-[#4C5A3E]' : 'bg-[#FAF8F5]/50'
                  }`} />
                </a>
              ))}
            </nav>

            {/* CTA button */}
            <button
              onClick={onJoinWaitlist}
              className={`hidden lg:flex items-center gap-2 px-6 py-2.5 font-mono text-[11px] uppercase tracking-[0.22em] border transition-all duration-500 ${
                scrolled
                  ? 'border-[#293932] text-[#293932] hover:bg-[#293932] hover:text-[#FAF8F5]'
                  : 'border-[#FAF8F5]/50 text-[#FAF8F5] hover:bg-[#FAF8F5]/10'
              }`}
            >
              Get in Touch
            </button>

            {/* Mobile toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`lg:hidden p-2 transition-colors duration-300 ${
                mobileOpen ? 'text-[#FAF8F5]' : scrolled ? 'text-[#293932]' : 'text-[#FAF8F5]'
              }`}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav"
            >
              {mobileOpen ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile full-screen menu — outside <header> to avoid stacking context issue */}
      <div
        id="mobile-nav"
        className={`lg:hidden fixed inset-0 bg-[#293932] z-[500] flex flex-col transition-all duration-500 ease-in-out ${
          mobileOpen ? 'opacity-100 pointer-events-auto translate-y-0' : 'opacity-0 pointer-events-none -translate-y-4'
        }`}
        aria-hidden={!mobileOpen}
      >
        {/* Mobile header row */}
        <div className="flex justify-between items-center h-[72px] px-6 sm:px-10 border-b border-[#FAF8F5]/10">
          <span className="font-display text-lg font-bold tracking-[0.12em] text-[#FAF8F5]">
            SALFORD & CO.
          </span>
          <button
            onClick={() => setMobileOpen(false)}
            className="p-2 text-[#FAF8F5]"
            aria-label="Close menu"
          >
            <X size={26} />
          </button>
        </div>

        <nav className="flex-1 flex flex-col justify-center px-8 gap-1" aria-label="Mobile navigation">
          {navLinks.map((link, i) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setMobileOpen(false)}
              className="py-4 border-b border-[#FAF8F5]/10 font-display text-3xl font-light text-[#FAF8F5] hover:text-[#C8BDAF] transition-colors duration-300"
              style={{ transitionDelay: `${i * 40}ms` }}
            >
              {link.label}
            </a>
          ))}
          <button
            onClick={() => { setMobileOpen(false); onJoinWaitlist?.(); }}
            className="mt-8 py-4 border border-[#FAF8F5]/40 text-[#FAF8F5] font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#FAF8F5]/10 transition-all duration-300"
          >
            Get in Touch
          </button>
        </nav>

        <div className="px-8 pb-10">
          <p className="font-mono text-[10px] text-[#FAF8F5]/40 uppercase tracking-[0.3em]">
            Rooted in Nature, Made for You
          </p>
        </div>
      </div>
    </>
  );
};

export default Navbar;
